import { Key, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { MultiBackend, TouchTransition } from 'react-dnd-multi-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorPage } from '@/components/common/ErrorPage';
import { Label, LABEL_TYPE } from '@/components/generic';
import { BUTTON_TYPE, ButtonConfig } from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { DetailsPageLayout } from '@/components/layouts';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PERMISSION_TYPE } from '@/enums';
import { useMobileScreen } from '@/hooks';
import { CeremonyTemplateIcon, EditPenIcon, TrashIcon } from '@/resources';
import { useDeleteCeremonyTemplateMutation, useGetCeremonyTemplateByIdQuery } from '@/services';
import { CeremonyTemplateSlot } from '@/types';
import { CeremonySlotType } from '@/types/CeremonyTemplateSlotTypes';

import { EventTemplateSlotCard } from './EventTemplateSlotCard';

export const EventTemplateDetails = () => {
  const [t] = useTranslation('common');
  const { eventTemplateId: id } = useParams();
  const isMobileView = useMobileScreen();

  const [error, setError] = useState<number>();
  const navigate = useNavigate();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [isReorderingEnabled, setIsReorderingEnabled] = useState<boolean>(false);

  const { data: ceremonyTemplate, isLoading: isLoadingCeremonyTemplate } =
    useGetCeremonyTemplateByIdQuery(Number(id), {
      onSuccess: () => undefined,
      onError: (error) => {
        setError(error.status);
      }
    });

  const dndBackends = {
    backends: [
      { backend: HTML5Backend },
      {
        backend: TouchBackend,
        options: { enableMouseEvents: true },
        preview: true,
        transition: TouchTransition
      }
    ]
  };

  const handleEditCeremonyTemplate = () => {
    navigate(`/ceremony-templates/${id}/edit`);
  };

  const handleDeleteCeremonyTemplate = () => {
    setIsOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  const { mutate: deleteCeremonyTemplate } = useDeleteCeremonyTemplateMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      navigate('/ceremony-templates');
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const getSortedSlots = () => {
    if (!ceremonyTemplate?.data.slots) {
      return [];
    }
    return [...ceremonyTemplate.data.slots].sort((a, b) => a.order - b.order);
  };

  const headerButtonsConfig: ButtonConfig[] = [
    {
      icon: <EditPenIcon className='text-primary-blue' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleEditCeremonyTemplate,
      permissions: [PERMISSION_TYPE.UPDATE_EVENT_TEMPLATE]
    },
    {
      icon: <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleDeleteCeremonyTemplate,
      permissions: [PERMISSION_TYPE.DELETE_EVENT_TEMPLATE]
    }
  ];

  return error ? (
    <ErrorPage code={error} />
  ) : (
    <DndProvider backend={MultiBackend} options={dndBackends}>
      <div className='flex h-full flex-col overflow-hidden'>
        <DetailsPageLayout
          childrenClassName='flex-1'
          icon={<CeremonyTemplateIcon className='h-4 stroke-[0.5] text-xl text-primary-blue' />}
          iconPath={'/ceremony-templates'}
          path={[{ label: t('ceremonyTemplates') }]}
          title={isMobileView ? `${t(ceremonyTemplate?.data?.name)}` : ''}
          isLoading={isLoadingCeremonyTemplate}
          headerButtons={headerButtonsConfig}>
          <div className='flex flex-1 flex-col md:flex-row'>
            <div className='flex w-full space-x-9 md:w-1/2'>
              <div className='flex w-full flex-col gap-4'>
                <div className='hidden text-xl font-semibold text-primary-blue md:block'>{`${t(ceremonyTemplate?.data?.name)}`}</div>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('organization')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                  />
                  <Label
                    text={`${t(ceremonyTemplate?.data?.organization?.name)}`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                </div>
              </div>
            </div>
            <div className='my-9 border-t border-secondary-blue-gray'></div>
            <div className='flex w-full flex-1 flex-col items-start overflow-hidden md:mb-12 md:max-w-[50%] md:flex-none'>
              <Label text={`${t('slots')}`} type={LABEL_TYPE.H3_BOLD} />
              <div className='no-scrollbar mt-5 flex w-full flex-1 basis-0 flex-wrap content-baseline overflow-y-auto pb-10 md:w-full'>
                {getSortedSlots().map(
                  (slot: { slotType: CeremonySlotType; id: Key | null | undefined }) => (
                    <EventTemplateSlotCard
                      type={slot.slotType as CeremonySlotType}
                      key={slot.id}
                      slot={slot as CeremonyTemplateSlot}
                      isEditEnabled={false}
                      isReorderingEnabled={isReorderingEnabled}
                      setIsReorderingEnabled={setIsReorderingEnabled}
                    />
                  )
                )}
              </div>
            </div>
          </div>
        </DetailsPageLayout>
        {isOpenDeleteModal && (
          <ConfirmationModal
            isOpen={isOpenDeleteModal}
            message={t('deleteMessage', { item: ceremonyTemplate?.data.name })}
            onClose={onCloseDeleteModal}
            onConfirm={() => deleteCeremonyTemplate(ceremonyTemplate?.data.id)}
          />
        )}
      </div>
    </DndProvider>
  );
};
